import { environment } from '../../../environments/environment';
import type {
  ChatMessage,
  ChatThread,
  StreamEvent,
  ThreadHistoryResponse,
  ThreadListResponse,
} from './types';

/**
 * The insight API, which serves Ask Derek. It is a service of its own, so
 * these are plain `fetch` calls with a bearer token rather than anything that
 * goes through Apollo — and the reply is a `text/event-stream`, which needs
 * the reader below rather than a JSON response.
 */

const BASE_URL = environment.insightApiUrl;

export type TokenGetter = () => Promise<string>;

async function authHeaders(getToken: TokenGetter): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    headers.Authorization = `Bearer ${await getToken()}`;
  } catch {
    // The screen is only reachable signed in, but a token that has gone stale
    // mid-session should produce the service's own 401 rather than an error
    // here that says nothing about why.
  }
  return headers;
}

function randomId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 11)}_${Date.now()}`;
}

export const newThreadId = () => randomId('thread');
export const newMessageId = () => randomId('msg');

/**
 * Streams one reply, calling `onEvent` per frame.
 *
 * `EventSource` cannot carry an Authorization header, so this reads the
 * response body itself. Frames can be split across chunks, so the tail of a
 * chunk is held back until the newline that ends it arrives — without that, a
 * long answer loses whichever tokens happen to land on a chunk boundary.
 */
export async function streamChat(
  message: string,
  threadId: string,
  getToken: TokenGetter,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${BASE_URL}/chat/stream`, {
    method: 'POST',
    headers: await authHeaders(getToken),
    body: JSON.stringify({ message, thread_id: threadId }),
    signal,
  });

  if (!response.ok) throw new Error(`Chat request failed: ${response.status}`);
  if (!response.body) throw new Error('Chat response had no body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        onEvent(JSON.parse(line.slice(6)) as StreamEvent);
      } catch {
        // A frame we cannot read is skipped; the rest of the reply still
        // arrives.
      }
    }
  }
}

export async function fetchThreadHistory(
  threadId: string,
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<ChatThread> {
  const response = await fetch(`${BASE_URL}/threads/${threadId}/history`, {
    headers: await authHeaders(getToken),
    signal,
  });
  if (!response.ok) throw new Error(`Could not load that conversation: ${response.status}`);

  const body = (await response.json()) as ThreadHistoryResponse;
  const messages: ChatMessage[] = body.messages.map((message) => ({
    id: newMessageId(),
    content: message.content || '',
    // The service names the two sides `human` and `ai`; the screen calls them
    // user and assistant.
    role: message.role === 'human' ? 'user' : 'assistant',
    timestamp: message.timestamp || new Date().toISOString(),
    toolCalls: message.tool_calls,
  }));
  return { id: threadId, messages };
}

export async function fetchThreadList(
  getToken: TokenGetter,
  { limit = 20, offset = 0 }: { limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<ThreadListResponse> {
  const response = await fetch(`${BASE_URL}/threads?limit=${limit}&offset=${offset}`, {
    headers: await authHeaders(getToken),
    signal,
  });
  if (!response.ok) throw new Error(`Could not load conversations: ${response.status}`);
  return (await response.json()) as ThreadListResponse;
}

export async function deleteThread(threadId: string, getToken: TokenGetter): Promise<void> {
  const response = await fetch(`${BASE_URL}/threads/${threadId}`, {
    method: 'DELETE',
    headers: await authHeaders(getToken),
  });
  if (!response.ok) throw new Error(`Could not delete that conversation: ${response.status}`);
}
